import { createSlice } from "@reduxjs/toolkit";
import { resetAll } from "./baseSlice";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";
import { licenseErrorMessageId } from "../../helpers/EnterpriseLicenseHelper";

const initialState = {
  data: [],
  record: null,
  requesting: false,
  error: null,
  action: null,
  // The licence, entitlements and on/off saves of the details screen. The
  // errors are the API's error codes; the dialogs show the ones they know.
  licenseSaving: false,
  licenseError: null,
  entitlementsSaving: false,
  entitlementsError: null,
  activating: false,
  query: {
    pageNo: 1,
    pageSize: 50,
    sortBy: "asc",
    sortColumn: "name",
    namePattern: "",
  },
};

const customIntl = intl();

const toastSomethingWrong = () =>
  toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));

// A refusal a dialog explains itself stays in the store; anything else (an
// unknown organisation, a new code, no answer) is the generic toast. A 401
// signs the admin out instead.
const errorCodeOf = (payload) => {
  const code = payload?.response || "SOMETHING_ELSE_WRONG";
  if (!licenseErrorMessageId(code) && payload?.status !== 401) {
    toastSomethingWrong();
  }
  return code;
};

// A new organisation's logo is uploaded first (EnterpriseService.create);
// the API refuses a file it cannot read as an image (INVALID_IMAGE) or one
// too large in pixels (IMAGE_TOO_LARGE) with a 400. The same messages as
// the other image uploads (uploadSlice).
const LOGO_ERROR_MESSAGES = {
  INVALID_IMAGE: "invalid_image_upload_error",
  IMAGE_TOO_LARGE: "image_too_large_upload_error",
};

const enterprisesSlice = createSlice({
  name: "enterprises",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    listRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "list";
      state.record = null;
    },
    listSuccess: (state, action) => {
      state.data = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    listFail: (state, action) => {
      state.data = [];
      state.requesting = false;
      state.error = action.payload;
    },
    createRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "create";
    },
    createSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "enterprise_create_success" })
      );
    },
    createFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      const logoMessageId = LOGO_ERROR_MESSAGES[action.payload?.response];
      if (logoMessageId) {
        toast.error(customIntl.formatMessage({ id: logoMessageId }));
      } else if (
        action.payload.status !== 400 &&
        action.payload.status !== 401
      ) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    enterpriseDetailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "enterprise_details";
      // A reload after a save keeps the organisation on screen meanwhile.
      if (state.record?.id !== action.payload?.id) {
        state.record = null;
      }
      // The store is persisted: a save cut off by a reload must not leave
      // its button spinning.
      state.licenseSaving = false;
      state.licenseError = null;
      state.entitlementsSaving = false;
      state.entitlementsError = null;
      state.activating = false;
    },
    enterpriseDetailsSuccess: (state, action) => {
      state.record = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    enterpriseDetailsFail: (state, action) => {
      state.record = null;
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateSelfEnterpriseDetailRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update";
    },
    updateSelfEnterpriseDetailSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "enterprise" }),
          }
        )
      );
    },
    updateSelfEnterpriseDetailFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    addLicenseRequest: (state) => {
      state.licenseSaving = true;
      state.licenseError = null;
    },
    addLicenseSuccess: (state) => {
      state.licenseSaving = false;
      toast.success(customIntl.formatMessage({ id: "license_added" }));
    },
    addLicenseFail: (state, action) => {
      state.licenseSaving = false;
      state.licenseError = errorCodeOf(action.payload);
    },
    updateEntitlementsRequest: (state) => {
      state.entitlementsSaving = true;
      state.entitlementsError = null;
    },
    updateEntitlementsSuccess: (state) => {
      state.entitlementsSaving = false;
      toast.success(customIntl.formatMessage({ id: "entitlements_updated" }));
    },
    updateEntitlementsFail: (state, action) => {
      state.entitlementsSaving = false;
      state.entitlementsError = errorCodeOf(action.payload);
    },
    // payload.action is "activate" or "deactivate".
    enterpriseActivationRequest: (state) => {
      state.activating = true;
    },
    enterpriseActivationSuccess: (state, action) => {
      state.activating = false;
      toast.success(
        customIntl.formatMessage({
          id:
            action.payload?.action === "activate"
              ? "enterprise_activated"
              : "enterprise_deactivated",
        })
      );
    },
    enterpriseActivationFail: (state, action) => {
      state.activating = false;
      if (action.payload?.status !== 401) {
        toastSomethingWrong();
      }
    },
    setQuery: (state, action) => {
      state.query = {
        ...state.query,
        ...action.payload,
      };
    },
  },
});

export const {
  addLicenseFail,
  addLicenseRequest,
  addLicenseSuccess,
  enterpriseActivationFail,
  enterpriseActivationRequest,
  enterpriseActivationSuccess,
  updateEntitlementsFail,
  updateEntitlementsRequest,
  updateEntitlementsSuccess,
  createFail,
  createRequest,
  createSuccess,
  listFail,
  listRequest,
  listSuccess,
  setQuery,
  enterpriseDetailsFail,
  enterpriseDetailsRequest,
  enterpriseDetailsSuccess,
  updateEnterpriseDetailFail,
  updateEnterpriseDetailRequest,
  updateEnterpriseDetailSuccess,
} = enterprisesSlice.actions;
export default enterprisesSlice.reducer;
